import type {Bullets, Fields, FontWeights, SectionData} from '../gutenberg/blocks/widget-styles/Section';
import type {Attr as NavigationAttr} from '../gutenberg/blocks/navigation/block';
import type {Attr as PagesAttr} from '../gutenberg/blocks/pages/Edit';
import type {Attr as CategoryAttr} from '../gutenberg/blocks/categories/Edit';
import type {ComponentClass, FunctionComponent, PropsWithChildren} from 'react';
import type {BlockAttributes, BlockSettings, CreateBlock} from '@wordpress/blocks';
import type {AccordionsEnabled, SvgInHtml} from '../accordions';
import type {LegacyObject} from '../accordions/legacy-asm-pro';
import type {IconType} from '../gutenberg/blocks/Accordion';

type AccordionStyle = {
	contract: string;
	expand: string;
	label: string;
}

type PreviewOptions = {
	attributes: NavigationAttr;
	block: string;
	clientId: string;
}

export type WPBoolean = '1' | '';

export type Screen = 'site-editor' | 'widgets' | 'post' | 'customize';

export interface JSConfig {
	blocks: {
		categories: {
			id: 'advanced-sidebar-menu/categories';
			attributes: BlockAttributes<CategoryAttr>;
			supports: BlockSettings<CategoryAttr>['supports'];
		};
		pages: {
			id: 'advanced-sidebar-menu/pages';
			attributes: BlockAttributes<PagesAttr>;
			supports: BlockSettings<PagesAttr>['supports'];
		};
		navigation: {
			id: 'advanced-sidebar-menu/navigation';
			attributes: BlockAttributes<NavigationAttr>;
			supports: BlockSettings<NavigationAttr>['supports'];
		}
	};
	categories: {
		excluded: number[];
		displayEach: {
			list: string;
			widget: string;
		};
		order: {
			ASC: string;
			DESC: string;
		};
		orderBy: { [ value: string ]: string }
	};
	displayPosts: {
		options: { [ value: string ]: string };
		types: { [ value: string ]: string };
	};
	docs: {
		page: string;
		category: string;
		navigation: string;
	};
	fields: {
		title: 'advanced-sidebar-menu/link-title';
		excludePage: 'advanced-sidebar-menu/exclude-page';
	};
	error: string;
	features: string[];
	ErrorBoundary: ComponentClass<PropsWithChildren<{
		attributes: object,
		block: string;
		section: string;
	}>>;
	isPostEdit: WPBoolean;
	isPro: WPBoolean;
	isWidgets: WPBoolean;
	menus: { [ value: number ]: string };
	pages: {
		excluded: number[];
		orderBy: { [ value: string ]: string };
	};
	postTypes: { [ value: string ]: string };
	Preview: FunctionComponent<PreviewOptions>;
	currentScreen: Screen;
	siteInfo: {
		basic: string;
		classicWidgets: boolean;
		php: string;
		pro: string;
		scriptDebug: boolean;
		wordpress: string;
	};
	styles: {
		bullets: Bullets;
		fields: Fields;
		sections: SectionData[];
		weights: FontWeights;
	};
	support: string;
	taxonomies: { [ value: string ]: string };
	transformLegacyWidget: ( name: string ) => ( widgetValues: {
		instance: object
	} ) => CreateBlock<NavigationAttr>;
}


declare global {
	interface Window {
		// @ts-ignore -- ADVANCED_SIDEBAR_MENU is defined twice because we import from the basic version.
		ADVANCED_SIDEBAR_MENU: JSConfig;
		ADVANCED_SIDEBAR_MENU_ACCORDIONS: {
			iconStyle: string;
			icons: Record<IconType, AccordionStyle>;
		};
		advancedSidebarMenuPro?: {
			accordions: {
				enabled: AccordionsEnabled;
				expandSubMenu: ( $menuItem: JQuery<HTMLLIElement>, $icon: JQuery<SvgInHtml> ) => void;
				init: ( force?: boolean ) => void;
			};
		};
		asm_pro?: LegacyObject;
		elementorFrontend?: {
			hooks: {
				addAction: ( action: string, callback: () => void ) => void;
			}
		};
		__TEST__?: boolean;
	}
}

// @ts-ignore -- ADVANCED_SIDEBAR_MENU is defined twice because we import from the basic version.
export const CONFIG: JSConfig = window.ADVANCED_SIDEBAR_MENU;
